/**
 * control-group.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { AbstractControl, FormArray, FormControl, FormGroup }
    from "@angular/forms";

import { ValidationBuilder } from "../../intake-form/validation.builder";
import { arraySwap, inputless } from "../../support/utilities/functions";
import { TypedDataHolder, TypedDataHolderWithDefaults, TypedData } from "./typed-data";
import { IntakeStep } from "app/algorithms/structure";
import { controlNameBinding } from "@angular/forms/src/directives/reactive_directives/form_control_name";

/**
 * The control group type defines the group's overall layout and guides the
 * user to understand how the subelements are semantically related.
 */
export type ControlGroupType =
    "tabular" | "collated" | "clustered" | "ordered" | "wrapped" | null;

export class ControlGroup {
    public steps : TypedDataHolder[] = [];
    constructor (public type : ControlGroupType, public name? : string)
    {
        // Nothing
    }

    public add (entry : TypedDataHolder) : boolean
    {
        this.steps.push(entry);
        return true;
    }

    public postAdd : () => void = null;

    public get enabled () : boolean
    {
        return true;
    }
}

interface Ordered {
    order : number;
}

export class OrderedControlGroup extends ControlGroup
{
    public name : string = "ordered";
    public steps : Array<TypedDataHolder & Ordered> = [];
    constructor (
        public min : number = 0,
        public max : number = Number.MAX_SAFE_INTEGER - 1
    )
    {
        super(null);
    }

    public setMax (max : number) : TypedDataHolder[]
    {
        this.max = max;
        const removed : TypedDataHolder[] =
            this.steps.filter((step : Ordered) => step.order > max);
        this.steps = this.steps.filter((step : Ordered) => step.order <= max);
        return removed;
    }

    public setMin (min : number) : TypedDataHolder[]
    {
        this.min = min;
        const removed : TypedDataHolder[] =
            this.steps.filter((step : Ordered) => step.order < min);
        this.steps = this.steps.filter((step : Ordered) => step.order >= min);
        return removed;
    }

    private ordered (entry : any)
        : entry is Ordered
    {
        return (
            entry
            && Number.isInteger(entry.order)
            && entry.order !== Number.MAX_SAFE_INTEGER
        );
    }

    public add (entry : TypedDataHolder) : boolean
    {
        if (!this.ordered(entry))
        {
            return false;
        }

        if (this.steps.length === 0)
        {
            this.steps = [entry];
            return true;
        }

        let i : number;
        for (i = 0; i < this.steps.length; i++)
        {
            if (entry.order < this.steps[i].order)
            {
                break;
            }
        }

        this.steps.splice(i, 0, entry as TypedDataHolder & Ordered);
        return true;
    }
}

interface ArrayBacked {
    control : FormArray;
}
export class TabularControlGroup extends ControlGroup
{
    public steps : Array<TypedDataHolder & ArrayBacked>;
    public commentStep : TypedDataHolder;
    public size : number = 0;
    public form : FormGroup = new FormGroup({});
    public gridStyle : boolean = true;
    constructor (
        name : string,
        public gridStyleInflection : number = Number.POSITIVE_INFINITY
    )
    {
        super("tabular", name);
    }

    public get enabled () : boolean
    {
        return !this.steps.some(
            (step : TypedDataHolder) =>
                step.hints && step.hints.editable === false);
    }

    private arrayBacked (entry : TypedDataHolder)
        : entry is TypedDataHolder & ArrayBacked
    {
        return (entry.control instanceof FormArray);
    }

    public add (entry : TypedDataHolder) : boolean
    {
        if (!this.arrayBacked(entry))
        {
            console.error(entry.control);
            throw new Error(`Cannot add column with non-FormArray control`);
        }
        if (!entry.hints || (typeof entry.hints.column !== "string"))
        {
            return false;
        }

        // TODO: if we end up setting array validators anywhere else,
        // then we'll have to stop blindly overwriting them here...
        entry.control.setValidators(ValidationBuilder.forTableColumn(entry));
        // provide a hook for the control builder to call autosize evts
        entry.hints._group = this;
        this.form.addControl(entry.hints.column, entry.control);

        const size : number = this.sizeFor(entry);
        if (size > this.size)
        {
            this.size = size;
        }
        this.steps.push(entry);
        this.autosize();

        if (entry.hints.commentColumn)
        {
            this.commentStep = entry;
        }

        if (this.steps.length === this.gridStyleInflection)
        {
            this.gridStyle = false;
        }

        this.steps.sort((a : any, b : any) => +a.order - +b.order);
        // this.columnNames = this.steps.map((step : any) => step.hints.column);
        return true;
    }

    public autosize (reevaluate : boolean = false) : void
    {
        if (reevaluate)
        {
            this.size = this.steps
                    .map((step : ArrayBacked & TypedDataHolder) =>
                        this.sizeFor(step))
                    .reduce(
                        (a : number, b : number) => Math.max(a, b));
        }

        this.steps.forEach((step : TypedDataHolder & ArrayBacked, i : number) =>
        {
            while (step.control.length < this.size)
            {
                step.control.push(new FormControl(
                    null,
                    ValidationBuilder.for(step)
                ));
            }
        });
    }

    private sizeFor (step : ArrayBacked & TypedDataHolder) : number
    {
        const control : FormArray = step.control;
        if (!control)
        {
            debugger;
        }
        const addNew : boolean = !step.hints || step.hints.editable !== false;
        for (let i : number = control.length - 1; i >= 0; i--)
        {
            if (!inputless(control.controls[i].value))
            {
                return addNew ? (i + 2) : (i + 1);
            }
        }
        return addNew ? 1 : 0;
    }

    public commentCount () : number
    {
        return this.steps.reduce(
            (prev : number, current : TypedDataHolder) =>
                prev + (current.comments ? current.comments.length : 0),
            0
        );
    }

    private assertRow (row : number) : void
    {
        if (!Number.isInteger(row) || row < 0 || row >= this.size)
        {
            console.error(this);
            throw new Error(`${row} is not a valid row index!`);
        }
    }

    public move (row : number, delta : number) : void
    {
        this.assertRow(row);
        this.assertRow(row + delta);
        const lower : number = Math.min(row, row + delta);
        this.steps.forEach(({control} : ArrayBacked) => {
            if (control.value[lower] !== control.value[lower + 1])
            {
                const swapped : any[] = arraySwap(control.value, lower);
                try {
                    control.setValue(swapped);
                    control.controls[lower].markAsDirty();
                    control.controls[lower + 1].markAsDirty();
                } catch (e) {
                    console.warn("swapped", swapped);
                    console.log(control);
                    throw e;
                }
            }
        });
    }

    public sortByName (stepName : string) : void
    {
        const i = this.steps.findIndex(
            (s : TypedDataHolder) => s.name === stepName);
        if (i !== -1)
        {
            this.sortBy(i);
        }
        else
        {
            throw new Error("Could not find step with name " + stepName);
        }
    }

    public sortBy (stepIndex : number) : void
    {
        const step : TypedDataHolder & ArrayBacked = this.steps[stepIndex];
        const permutation : number[] = getSortPermutation(step);
        this.steps.forEach((s : IntakeStep & ArrayBacked) => {
            console.log(s.control.value);
            const vals : any[] =
                applySortPermutation(s.control.value, permutation);
            console.log("after sort:", vals);
            s.control.setValue(vals);
            s.control.controls.forEach(
                (c : AbstractControl) => c.markAsDirty());
        });
    }

    public remove (row : number) : void
    {
        this.assertRow(row);
        this.steps.forEach(
            ({control} : ArrayBacked) => control.removeAt(row));
        this.size--;
    }
}

function getSortPermutation (host : TypedDataHolder & ArrayBacked) : number[]
{
    const permutation : number[] = Array.apply(
            null, {length: host.value.length}
        ).map((_ : any, i : number) => i);
    console.info(permutation);

    permutation.sort(
        (a : number, b : number) => TypedData.compareIndices(host, a, b));

    console.warn("PERM:", permutation);
    return permutation;
}

function applySortPermutation<T> (arr : T[], permutation : number[]) : T[]
{
    const out : T[] = new Array(arr.length);
    for (let i : number = 0; i < permutation.length; i++)
    {
        out[i] = arr[permutation[i]];
    }
    if (arr.length > permutation.length)
    {
        console.log(`Array exceeds given permutation by ${arr.length - permutation.length}`);
        for (let i : number = permutation.length; i < arr.length; i++)
        {
            out[i] = arr[i];
        }
    }
    return out;
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/control-group.ts